/**
 * String utilities — cleaning, formatting, initials extraction.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/string.jsx
 * - packages/hoi-poi-ui/src/utils/string.jsx
 * - apps/ssm-web4/src/app/utils/strings/index.js
 * - apps/ssm-admin/src/utils/string.jsx
 *
 * NOTE: React-dependent functions (breakLines) are intentionally excluded —
 * they stay in the packages/apps that need React.
 */

/**
 * Remove diacritical marks (accents) from a string using Unicode NFD
 * normalization. Also trims whitespace.
 *
 * Exported under both names for backward compatibility:
 * - `removeAccents` (hoi-poi-ui / web4 name)
 * - `cleanString` (ssm-web5 / admin name)
 *
 * @param {string} text
 * @returns {string}
 */
export const removeAccents = (text) =>
    text
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

/** Alias — some consumers know this as `cleanString`. */
export const cleanString = removeAccents;

/**
 * Sanitize a field name: strip accents, replace spaces/hyphens with `_`,
 * remove characters not matching the pattern, collapse consecutive `_`, and
 * optionally lower-case the result.
 *
 * @param {string} fieldName
 * @param {string} [pattern] — regex char-class pattern (default: `[^a-zA-Z0-9_]`)
 * @param {boolean} [forceLowerCase=true]
 * @returns {string}
 */
export function normalizeFieldName(fieldName, pattern, forceLowerCase = true) {
    const patt = new RegExp(pattern || '[^a-zA-Z0-9_]');
    let val = '';

    let name = fieldName.trim().replace(/\s|-/g, '_');
    name = removeAccents(name);

    name.split('').forEach((letter) => {
        if (!patt.test(letter)) {
            val += letter;
        }
    });

    // collapse consecutive underscores
    val = val.replace(/_+/g, '_');

    if (forceLowerCase) val = val.toLowerCase();
    return val;
}

/**
 * Capitalize the first character of a string.
 *
 * @param {string} item
 * @returns {string}
 */
export const capitalize = (item) => item.charAt(0).toUpperCase() + item.slice(1);

/**
 * Extract the first and last initials from a full name string.
 *
 * @param {string} name
 * @returns {string} Up to two uppercase letters (e.g. "JD")
 */
export const getFirstTwoInitials = (name) => {
    if (!name) return '';
    let initials = name.match(/\b\w/g) || [];
    const firstInitial = initials.shift();
    const lastInitial = initials.pop();
    initials = [firstInitial, lastInitial];
    initials = initials.join('').toUpperCase().substring(0, 2);
    return initials;
};

/**
 * Replace whole-word occurrences of `search` inside `obj` with `replace`,
 * preserving surrounding punctuation and whitespace.
 *
 * @param {string} obj — the text to search within
 * @param {string} search — the word to find
 * @param {string} replace — the replacement word
 * @returns {string}
 */
export const replaceWords = (obj, search, replace) => {
    const regExp = new RegExp(`(^|[^a-zA-ZΆΈ-ώἀ-ῼ\\n])(${search})(?![a-zA-ZΆΈ-ώἀ-ῼ])`, 'gm');
    const retStr = obj.replace(regExp, (match) => {
        if (match.length === 0 || replace.length === 0) {
            return replace;
        }

        let rpl = replace;
        const regex = new RegExp("(\\s|[!@#$%\\^&*)(+=._-]|'|')");

        const firstCh = match[0];
        const firstRplCh = replace[0];
        if (regex.test(firstCh) === true && firstCh !== firstRplCh) {
            rpl = `${firstCh}${rpl}`;
        }

        const lastCh = match[match.length - 1];
        const lastRplCh = replace[replace.length - 1];
        if (regex.test(lastCh) === true && lastCh !== lastRplCh) {
            rpl = `${rpl}${lastCh}`;
        }
        return rpl;
    });
    return retStr;
};

/**
 * Regex to test whether a string is a URL (starting with http(s):// or www.).
 * @type {RegExp}
 */
export const URL_REGEX = /^\b(((https?:\/\/)|(www\.))(\S+))$/;
